import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from './storage.service';

@Injectable({
    providedIn: 'root'
})
export class WelcomeDialogService {
    private readonly USER_NICKNAME_KEY = 'user_nickname';
    private readonly WELCOME_SHOWN_KEY = 'welcome_shown';

    constructor(
        private alertController: AlertController,
        private storageService: StorageService,
        private translate: TranslateService
    ) { }

    /**
     * Check if welcome dialog has been shown and user nickname is set
     */
    async shouldShowWelcome(): Promise<boolean> {
        const welcomeShown = await this.storageService.get(this.WELCOME_SHOWN_KEY);
        const userNickname = await this.storageService.get(this.USER_NICKNAME_KEY);

        return welcomeShown !== 'true' || !userNickname;
    }

    /**
     * Show welcome dialog to collect user nickname
     */
    async showWelcomeDialog(): Promise<string | null> {
        return new Promise(async (resolve) => {
            const alert = await this.alertController.create({
                header: this.translate.instant('WELCOME.TITLE'),
                subHeader: this.translate.instant('WELCOME.SUBHEADER'),
                message: this.translate.instant('WELCOME.MESSAGE'),
                inputs: [
                    {
                        name: 'nickname',
                        type: 'text',
                        placeholder: this.translate.instant('WELCOME.PLACEHOLDER'),
                        attributes: {
                            maxlength: 20,
                            required: true
                        }
                    }
                ],
                buttons: [
                    {
                        text: this.translate.instant('WELCOME.SKIP'),
                        role: 'cancel',
                        handler: async () => {
                            try {
                                await this.setWelcomeShown();
                                setTimeout(() => resolve(null), 100);
                            } catch (error) {
                                console.error('Error in skip handler:', error);
                                resolve(null);
                            }
                            return true; // Allow automatic dismiss
                        }
                    },
                    {
                        text: this.translate.instant('WELCOME.SAVE'),
                        handler: async (data) => {
                            const nickname = data.nickname?.trim();
                            if (!nickname) {
                                return false; // Keep dialog open
                            }

                            try {
                                await this.saveUserNickname(nickname);
                                await this.setWelcomeShown();
                                setTimeout(() => resolve(nickname), 100);
                            } catch (error) {
                                console.error('Failed to save nickname:', error);
                                setTimeout(() => resolve(null), 100);
                            }
                            return true; // Allow automatic dismiss
                        }
                    }
                ],
                backdropDismiss: false,
                cssClass: 'welcome-dialog'
            });

            await alert.present();

            // Properly handle dismissal to clean up any stale backdrops
            alert.onDidDismiss().then(() => {
                this.cleanupStaleBackdrops();
            });
        });
    }

    /**
     * Get the saved user nickname
     */
    async getUserNickname(): Promise<string | null> {
        const nickname = await this.storageService.get(this.USER_NICKNAME_KEY);
        return nickname;
    }

    /**
     * Save user nickname
     */
    async saveUserNickname(nickname: string): Promise<void> {
        await this.storageService.set(this.USER_NICKNAME_KEY, nickname.trim());
    }

    /**
     * Mark welcome dialog as shown
     */
    private async setWelcomeShown(): Promise<void> {
        await this.storageService.set(this.WELCOME_SHOWN_KEY, 'true');
    }

    /**
     * Reset welcome state (for testing)
     */
    async resetWelcome(): Promise<void> {
        await this.storageService.remove(this.WELCOME_SHOWN_KEY);
        await this.storageService.remove(this.USER_NICKNAME_KEY);
    }

    /**
     * Update user nickname (show edit dialog)
     */
    async editUserNickname(): Promise<string | null> {
        const currentNickname = await this.getUserNickname();

        return new Promise(async (resolve) => {
            const alert = await this.alertController.create({
                header: this.translate.instant('WELCOME.EDIT_TITLE'),
                message: this.translate.instant('WELCOME.EDIT_MESSAGE'),
                inputs: [
                    {
                        name: 'nickname',
                        type: 'text',
                        placeholder: this.translate.instant('WELCOME.PLACEHOLDER'),
                        value: currentNickname || '',
                        attributes: {
                            maxlength: 20,
                            required: true
                        }
                    }
                ],
                buttons: [
                    {
                        text: this.translate.instant('COMMON.CANCEL'),
                        role: 'cancel',
                        handler: async () => {
                            setTimeout(() => resolve(null), 100);
                            return true; // Allow automatic dismiss
                        }
                    },
                    {
                        text: this.translate.instant('WELCOME.SAVE'),
                        handler: async (data) => {
                            const nickname = data.nickname?.trim();
                            if (!nickname) {
                                return false;
                            }

                            try {
                                await this.saveUserNickname(nickname);
                                setTimeout(() => resolve(nickname), 100);
                            } catch (error) {
                                console.error('Failed to save nickname:', error);
                                setTimeout(() => resolve(null), 100);
                            }
                            return true; // Allow automatic dismiss
                        }
                    }
                ]
            });

            await alert.present();

            // Properly handle dismissal to clean up any stale backdrops
            alert.onDidDismiss().then(() => {
                this.cleanupStaleBackdrops();
            });
        });
    }

    private cleanupStaleBackdrops(): void {
        console.log('WelcomeDialog: Cleaning up stale alert backdrops...');

        // Remove any stale alert backdrops
        const staleAlertBackdrops = document.querySelectorAll('ion-alert ion-backdrop, .alert-backdrop');
        if (staleAlertBackdrops.length > 0) {
            console.log('WelcomeDialog: Removing', staleAlertBackdrops.length, 'stale alert backdrops');
            staleAlertBackdrops.forEach(backdrop => {
                backdrop.remove();
            });
        }

        // Remove any hidden or aria-hidden backdrops
        const hiddenBackdrops = document.querySelectorAll('ion-backdrop[aria-hidden="true"], ion-backdrop[hidden]');
        if (hiddenBackdrops.length > 0) {
            console.log('WelcomeDialog: Removing', hiddenBackdrops.length, 'hidden backdrops');
            hiddenBackdrops.forEach(backdrop => {
                backdrop.remove();
            });
        }

        console.log('WelcomeDialog: Stale backdrop cleanup completed');
    }
} 